import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Select from "react-select";
import { saveAs } from "file-saver";
import "./ManutenzioneTagliandi.css";
import RotateDevice from "../components/RotateDevice/RotateDevice";
import Lf23Loader from "../components/Lf23Loader";
// Bottone tutto schermo gia' usato in Gestione Percorsi: si importa invece di copiarlo
// (il suo CSS e' ridefinito in ManutenzioneTagliandi.css sotto lo scope della tab).
import { API_URL, authHeaders, FullscreenBtn } from "./GestionePercorsi/comuni";

/*
 * Tab "Tagliandi" di Manutenzione automezzi: situazione dei tagliandi a km mezzo per mezzo.
 * I numeri arrivano da dwh.scadenze_tagliandi, che la DAG Trans_scadenziario_tagliandi_interacta
 * riscrive ogni mattina: stato, km mancanti e data stimata sono gia' calcolati dal backend.
 *
 * La tab vive dentro .manutenzione-automezzi-modern (la pagina madre), quindi usa le sue classi
 * (toggle-filters-btn, filter-panel, stat-card-compact, column-toggle-btn, data-card-compact...):
 * stessa resa degli altri due tab senza ridefinire niente.
 */

// Indirizzo web di un post Interacta, per aprire la scadenza con un click
// (formato verificato da un post reale: .../portal/post/<id>).
const INTERACTA_POST_URL = (id) => `https://lafraternita.app.interacta.space/portal/post/${id}`;

// Stati prodotti da manutenzioneController, nell'ordine di urgenza.
const STATI = ["Scaduto", "In scadenza", "Entro 30 giorni", "In regola", "Da verificare", "Non monitorato"];
const CLASSE_STATO = {
    "Scaduto": "danger", "In scadenza": "warning", "Entro 30 giorni": "info",
    "In regola": "success", "Da verificare": "amber", "Non monitorato": "neutral",
};

const SCADENZA_APERTA = "Aperta su Interacta";
const SCADENZA_NESSUNA = "Nessuna";
const RIGHE_PER_PAGINA = [10, 20, 50, 100];
const FILTRI_VUOTI = { cerca: "", tipologie: [], sottotipologie: [], scadenza: [] };
// La DAG gira alle 06:30: prima di quest'ora e' normale vedere ancora i dati di ieri.
const ORA_ATTESA_DATI = 8;

const nf = (n) => (n === null || n === undefined ? "—" : Number(n).toLocaleString("it-IT"));
const dataIt = (iso) => (iso ? iso.split("-").reverse().join("/") : "—");
const opzioni = (valori) => valori.map((v) => ({ value: v, label: v }));

// Stessa icona delle card degli altri tab (ManutenzioneAutomezzi.getVehicleIcon).
const iconaMezzo = (tipologia) => (
    tipologia?.toLowerCase().includes("trasporto persone") || tipologia?.toLowerCase().includes("van") ? "🚐" : "🚚"
);

// Colonne ordinabili: chiave della riga + tipo di confronto.
const COLONNE = [
    { key: "targa", label: "Targa" },
    { key: "tipologia", label: "Tipologia" },
    { key: "data_ultimo", label: "Ultimo tagliando" },
    { key: "km_oggi", label: "Km attuali", numeric: true },
    { key: "soglia", label: "Prossimo a", numeric: true },
    { key: "residuo", label: "Km mancanti", numeric: true },
    { key: "percentuale", label: "Intervallo percorso", numeric: true },
    { key: "data_stimata", label: "Data stimata" },
    { key: "ordine_stato", label: "Stato", numeric: true },
    { key: "scadenza_stato", label: "Scadenza Interacta" },
    { key: "motivo", label: "Note", notSortable: true },
];

// Confronto per l'ordinamento: i valori mancanti vanno sempre in fondo, in entrambe le direzioni.
const confronta = (a, b, numeric) => {
    const va = a === null || a === undefined || a === "";
    const vb = b === null || b === undefined || b === "";
    if (va || vb) return va === vb ? 0 : va ? 1 : -1;
    if (numeric) return a - b;
    return String(a).localeCompare(String(b), "it-IT", { numeric: true });
};

// I dati sono "vecchi" se non sono di oggi e il calcolo del mattino avrebbe gia' dovuto girare,
// oppure se sono piu' vecchi di ieri a qualunque ora. Date e ore in fuso italiano.
const datiNonAggiornati = (dataCalcolo) => {
    if (!dataCalcolo) return false;
    const adesso = new Date(new Date().toLocaleString("en-US", { timeZone: "Europe/Rome" }));
    const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    const oggi = iso(adesso);
    const ieri = iso(new Date(adesso.getFullYear(), adesso.getMonth(), adesso.getDate() - 1));
    return dataCalcolo < ieri || (dataCalcolo < oggi && adesso.getHours() >= ORA_ATTESA_DATI);
};

// Spunta delle chip: stessa icona delle chip "Cantieri" degli altri tab.
const IconaChip = ({ attiva }) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        {attiva ? (
            <>
                <path d="M9 11l3 3L22 4"></path>
                <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
            </>
        ) : (
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
        )}
    </svg>
);

// Stato della scadenza Interacta: link al post se l'indirizzo e' configurato.
const ScadenzaInteracta = ({ r }) => {
    if (!r.scadenza_stato) return <span className="mt-muted">—</span>;
    if (INTERACTA_POST_URL && r.scadenza_post_id) {
        return (
            <a className="mt-scadenza mt-scadenza-link" href={INTERACTA_POST_URL(r.scadenza_post_id)}
                target="_blank" rel="noopener noreferrer" title={`Apri il post ${r.scadenza_post_id} su Interacta`}>
                {r.scadenza_stato}
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                    <polyline points="15 3 21 3 21 9"></polyline><line x1="10" y1="14" x2="21" y2="3"></line>
                </svg>
            </a>
        );
    }
    return <span className="mt-scadenza" title={`Post Interacta ${r.scadenza_post_id}`}>{r.scadenza_stato}</span>;
};

// Barra dell'intervallo percorso (riusata in tabella e card).
const BarraIntervallo = ({ r }) => (r.percentuale === null ? "—" : (
    <div className="mt-barra" title={`${r.percentuale}% dell'intervallo percorso`}>
        <div className="mt-barra-sfondo">
            <div className={`mt-barra-pieno mt-${CLASSE_STATO[r.stato]}`} style={{ width: `${Math.min(Math.max(r.percentuale, 0), 100)}%` }} />
        </div>
        <span>{r.percentuale}%</span>
    </div>
));

const testoKmMancanti = (r) => (
    r.residuo === null ? "—" : r.residuo > 0 ? `${nf(r.residuo)} km` : `superato di ${nf(-r.residuo)} km`
);

const ManutenzioneTagliandi = () => {
    const [dati, setDati] = useState({ data_calcolo: null, righe: [] });
    const [loading, setLoading] = useState(true);
    const [errore, setErrore] = useState(null);

    const [showFilters, setShowFilters] = useState(false);
    const [viewMode, setViewMode] = useState("table");
    // Campi del pannello filtri: sono una BOZZA, la tabella cambia solo con "Applica".
    const [cerca, setCerca] = useState("");
    const [tipologie, setTipologie] = useState([]);
    const [sottotipologie, setSottotipologie] = useState([]);
    const [scadenza, setScadenza] = useState([]);
    // Filtri applicati davvero alla tabella (copia della bozza al click su "Applica").
    const [filtri, setFiltri] = useState(FILTRI_VUOTI);
    const [statiAttivi, setStatiAttivi] = useState(STATI);  // chip rapide: tutte accese all'inizio

    // Default: i piu' urgenti in alto (ordine_stato), a parita' di stato i km mancanti.
    const [ordine, setOrdine] = useState({ key: "ordine_stato", dir: "asc" });
    const [pagina, setPagina] = useState(1);
    const [perPagina, setPerPagina] = useState(20);
    const [fullscreen, setFullscreen] = useState(false);
    const [esportando, setEsportando] = useState(false);

    useEffect(() => {
        axios.get(`${API_URL}/interacta/manutenzione-automezzi/tagliandi`, { headers: authHeaders() })
            .then((res) => setDati(res.data))
            .catch((err) => {
                console.error("Errore caricamento tagliandi:", err);
                setErrore("Impossibile caricare la situazione dei tagliandi.");
            })
            .finally(() => setLoading(false));
    }, []);

    // ESC chiude il tutto schermo (stesso pattern degli altri tab).
    useEffect(() => {
        if (!fullscreen) return undefined;
        const onEsc = (e) => { if (e.key === "Escape") setFullscreen(false); };
        document.addEventListener("keydown", onEsc);
        return () => document.removeEventListener("keydown", onEsc);
    }, [fullscreen]);

    const righe = dati.righe;

    // Opzioni dei filtri ricavate dai dati, cosi' non mostrano mai voci senza mezzi.
    const tipologieUniche = useMemo(() => [...new Set(righe.map((r) => r.tipologia).filter(Boolean))].sort(), [righe]);
    const sottotipologieUniche = useMemo(() => [...new Set(righe.map((r) => r.sottotipologia).filter(Boolean))].sort(), [righe]);

    // Conteggi su tutta la flotta (KPI e chip): servono da panoramica, non seguono i filtri.
    const conteggi = useMemo(() => {
        const c = Object.fromEntries(STATI.map((s) => [s, 0]));
        let aperte = 0;
        for (const r of righe) {
            c[r.stato] = (c[r.stato] || 0) + 1;
            if (r.scadenza_stato) aperte += 1;
        }
        return { ...c, aperte };
    }, [righe]);

    const filtrate = useMemo(() => {
        const testo = filtri.cerca.trim().toUpperCase();
        const col = COLONNE.find((x) => x.key === ordine.key);
        const verso = ordine.dir === "asc" ? 1 : -1;
        return righe
            .filter((r) => !testo || r.targa.includes(testo))
            .filter((r) => !filtri.tipologie.length || filtri.tipologie.includes(r.tipologia))
            .filter((r) => !filtri.sottotipologie.length || filtri.sottotipologie.includes(r.sottotipologia))
            .filter((r) => !filtri.scadenza.length || filtri.scadenza.includes(r.scadenza_stato ? SCADENZA_APERTA : SCADENZA_NESSUNA))
            .filter((r) => statiAttivi.includes(r.stato))
            .sort((a, b) => verso * confronta(a[ordine.key], b[ordine.key], col?.numeric)
                || confronta(a.residuo, b.residuo, true)
                || a.targa.localeCompare(b.targa));
    }, [righe, filtri, statiAttivi, ordine]);

    // Ogni cambio di filtri o ordinamento riporta alla prima pagina: restare a pagina 5
    // di un elenco che ora ne ha 2 mostrerebbe una tabella vuota.
    useEffect(() => { setPagina(1); }, [filtri, statiAttivi, ordine, perPagina]);

    const totalePagine = Math.max(1, Math.ceil(filtrate.length / perPagina));
    const righePagina = filtrate.slice((pagina - 1) * perPagina, pagina * perPagina);

    const applicaFiltri = () => setFiltri({ cerca, tipologie, sottotipologie, scadenza });

    // Reset svuota sia la bozza sia i filtri applicati (e riaccende tutte le chip).
    const resetFiltri = () => {
        setCerca(""); setTipologie([]); setSottotipologie([]); setScadenza([]);
        setFiltri(FILTRI_VUOTI);
        setStatiAttivi(STATI);
    };

    // Chip stato: accende/spegne uno stato. "Seleziona tutti" le riaccende tutte.
    const tuttiAttivi = statiAttivi.length === STATI.length;
    const toggleStato = (stato) => setStatiAttivi((s) => (
        s.includes(stato) ? s.filter((x) => x !== stato) : STATI.filter((x) => x === stato || s.includes(x))
    ));

    const ordinaPer = (key) => setOrdine((o) => (
        o.key === key ? { key, dir: o.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }
    ));

    // Descrizione leggibile di cio' che si sta guardando: va in testa al file Excel.
    const descrizioneFiltri = () => {
        const parti = [];
        if (filtri.cerca.trim()) parti.push(`targa contiene "${filtri.cerca.trim().toUpperCase()}"`);
        if (filtri.tipologie.length) parti.push(`tipologia: ${filtri.tipologie.join(", ")}`);
        if (filtri.sottotipologie.length) parti.push(`sottotipologia: ${filtri.sottotipologie.join(", ")}`);
        if (filtri.scadenza.length) parti.push(`scadenza Interacta: ${filtri.scadenza.join(", ")}`);
        if (!tuttiAttivi) parti.push(`stato: ${statiAttivi.join(", ") || "nessuno"}`);
        return parti.join(" · ");
    };

    // Il file contiene TUTTE le righe filtrate (non solo la pagina), nello stesso ordine della tabella.
    const esporta = async () => {
        if (esportando || filtrate.length === 0) return;
        setEsportando(true);
        try {
            const res = await axios.post(
                `${API_URL}/interacta/manutenzione-automezzi/tagliandi/export`,
                { righe: filtrate, dataCalcolo: dati.data_calcolo, filtri: descrizioneFiltri() },
                { headers: authHeaders(), responseType: "blob" },
            );
            const nome = res.headers["content-disposition"]?.match(/filename=([^;]+)/)?.[1]?.trim();
            saveAs(res.data, nome || "tagliandi.xlsx");
        } catch (err) {
            console.error("Errore export tagliandi:", err);
            alert("Errore durante l'esportazione del file Excel.");
        } finally {
            setEsportando(false);
        }
    };

    if (loading) return <Lf23Loader />;
    if (errore) return <div className="manutenzione-tagliandi"><div className="mt-errore">{errore}</div></div>;

    const vecchi = datiNonAggiornati(dati.data_calcolo);

    // KPI: stesse card compatte degli altri tab, solo informative (i filtri sono le chip sotto).
    const kpi = [
        {
            label: "Scaduti", valore: conteggi["Scaduto"], cls: "stat-danger",
            icona: <><circle cx="12" cy="12" r="10"></circle><line x1="15" y1="9" x2="9" y2="15"></line><line x1="9" y1="9" x2="15" y2="15"></line></>,
        },
        {
            label: "In scadenza (≤ 500 km)", valore: conteggi["In scadenza"], cls: "stat-warning",
            icona: <><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"></path><line x1="12" y1="9" x2="12" y2="13"></line><line x1="12" y1="17" x2="12.01" y2="17"></line></>,
        },
        {
            label: "Entro 30 giorni", valore: conteggi["Entro 30 giorni"], cls: "stat-info",
            icona: <><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></>,
        },
        {
            label: "Scadenza aperta su Interacta", valore: conteggi.aperte, cls: "stat-primary",
            icona: <><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"></path></>,
        },
        {
            label: "Non monitorati", valore: conteggi["Non monitorato"], cls: "stat-neutral",
            icona: <><circle cx="12" cy="12" r="10"></circle><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"></line></>,
        },
    ];

    const paginazione = (
        <div className="mt-pagination">
            <button className="mt-page-btn" disabled={pagina <= 1} onClick={() => setPagina((p) => p - 1)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 18l-6-6 6-6" /></svg> Prec
            </button>
            <span className="mt-page-info">Pag <strong>{pagina}</strong> di <strong>{totalePagine}</strong></span>
            <span className="mt-page-total">({nf(filtrate.length)} mezzi)</span>
            <button className="mt-page-btn" disabled={pagina >= totalePagine} onClick={() => setPagina((p) => p + 1)}>
                Succ <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18l6-6-6-6" /></svg>
            </button>
            <div className="mt-perpage">
                <span>Per pagina:</span>
                {RIGHE_PER_PAGINA.map((n) => (
                    <button key={n} className={`mt-perpage-btn${perPagina === n ? " active" : ""}`} onClick={() => setPerPagina(n)}>{n}</button>
                ))}
            </div>
        </div>
    );

    // Stesso aspetto dell'ExportBtn di Gestione Percorsi, ma disattivabile quando non ci sono righe.
    const bottoneExport = (
        <button className="btn btn-success gp-new-btn" onClick={esporta} disabled={esportando || filtrate.length === 0}
            title={filtrate.length === 0 ? "Nessun mezzo da esportare con questi filtri" : "Scarica in Excel tutte le righe filtrate, nell'ordine della tabella"}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            {esportando ? "Esportazione…" : "Esporta Excel"}
        </button>
    );

    return (
        <div className="manutenzione-tagliandi">
            {dati.data_calcolo && (vecchi ? (
                <div className="mt-avviso-dati" role="alert">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"></path>
                        <line x1="12" y1="9" x2="12" y2="13"></line><line x1="12" y1="17" x2="12.01" y2="17"></line>
                    </svg>
                    <span>
                        <strong>Dati non aggiornati:</strong> la situazione è quella del <strong>{dataIt(dati.data_calcolo)}</strong>.
                        Il calcolo automatico di stamattina (ore 06:30) non risulta arrivato: km e scadenze potrebbero
                        non essere quelli di oggi. Se il problema persiste, segnalalo a chi gestisce le automazioni.
                    </span>
                </div>
            ) : (
                <div className="posizione-freshness">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline>
                    </svg>
                    <span>
                        Dati aggiornati al <strong>{dataIt(dati.data_calcolo)}</strong>
                        <span className="posizione-freshness-dettaglio"> · ricalcolati ogni mattina dai km Webfleet e dallo scadenziario Interacta</span>
                    </span>
                </div>
            ))}

            {/* Filtri a scomparsa + vista tabella/card, sopra le KPI come negli altri tab */}
            <div className="controls-section">
                <button className={`toggle-filters-btn ${showFilters ? "active" : ""}`} onClick={() => setShowFilters(!showFilters)}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
                    </svg>
                    {showFilters ? "Nascondi Filtri" : "Mostra Filtri"}
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                </button>

                {showFilters && (
                    <div className="filter-panel">
                        <div className="filter-grid">
                            <div className="filter-group">
                                <label>Cerca per targa</label>
                                <div className="search-box-filter">
                                    <svg className="search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <circle cx="11" cy="11" r="8"></circle><path d="m21 21-4.35-4.35"></path>
                                    </svg>
                                    <input type="text" className="search-input" placeholder="Inserisci targa..." value={cerca}
                                        onChange={(e) => setCerca(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === "Enter") applicaFiltri(); }} />
                                </div>
                            </div>
                            <div className="filter-group">
                                <label>Tipologia</label>
                                <Select isMulti placeholder="Tutte le tipologie" options={opzioni(tipologieUniche)}
                                    value={opzioni(tipologie)} onChange={(sel) => setTipologie(sel ? sel.map((s) => s.value) : [])}
                                    classNamePrefix="filter-select" closeMenuOnSelect={false} />
                            </div>
                            <div className="filter-group">
                                <label>Sottotipologia</label>
                                <Select isMulti placeholder="Tutte le sottotipologie" options={opzioni(sottotipologieUniche)}
                                    value={opzioni(sottotipologie)} onChange={(sel) => setSottotipologie(sel ? sel.map((s) => s.value) : [])}
                                    classNamePrefix="filter-select" closeMenuOnSelect={false} />
                            </div>
                            <div className="filter-group">
                                <label>Scadenza Interacta</label>
                                <Select isMulti placeholder="Tutte" options={opzioni([SCADENZA_APERTA, SCADENZA_NESSUNA])}
                                    value={opzioni(scadenza)} onChange={(sel) => setScadenza(sel ? sel.map((s) => s.value) : [])}
                                    classNamePrefix="filter-select" closeMenuOnSelect={false} />
                            </div>
                        </div>
                        <div className="filter-actions">
                            <button className="filter-reset-btn" onClick={resetFiltri}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"></path><path d="M3 3v5h5"></path>
                                </svg>
                                Reset
                            </button>
                            <button className="filter-apply-btn" onClick={applicaFiltri}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <polyline points="20 6 9 17 4 12"></polyline>
                                </svg>
                                Applica
                            </button>
                        </div>
                    </div>
                )}

                {/* Vista tabella/card + conteggio: stesso blocco degli altri tab */}
                <div className="controls-compact">
                    <div className="controls-right">
                        <div className="view-toggle-compact">
                            <button className={`view-btn-compact ${viewMode === "table" ? "active" : ""}`} onClick={() => setViewMode("table")}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <rect x="3" y="3" width="7" height="7"></rect><rect x="14" y="3" width="7" height="7"></rect>
                                    <rect x="14" y="14" width="7" height="7"></rect><rect x="3" y="14" width="7" height="7"></rect>
                                </svg>
                                Tabella
                            </button>
                            <button className={`view-btn-compact ${viewMode === "cards" ? "active" : ""}`} onClick={() => setViewMode("cards")}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <rect x="3" y="3" width="7" height="7"></rect><rect x="14" y="3" width="7" height="7"></rect>
                                    <rect x="14" y="14" width="7" height="7"></rect><rect x="3" y="14" width="7" height="7"></rect>
                                </svg>
                                Cards
                            </button>
                        </div>
                        <div className="results-badge">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle>
                                <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"></path>
                            </svg>
                            {filtrate.length} veicol{filtrate.length === 1 ? "o" : "i"}
                        </div>
                        {viewMode === "cards" && bottoneExport}
                    </div>
                </div>
            </div>

            {/* KPI */}
            <div className="stats-grid-compact stats-grid-5">
                {kpi.map((k) => (
                    <div key={k.label} className={`stat-card-compact ${k.cls}`}>
                        <div className="stat-icon-compact">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">{k.icona}</svg>
                        </div>
                        <div className="stat-content-compact">
                            <p className="stat-label-compact">{k.label}</p>
                            <h3 className="stat-value-compact">{nf(k.valore)}</h3>
                        </div>
                    </div>
                ))}
            </div>

            {/* Chip rapide per stato, come le chip "Cantieri" degli altri tab */}
            <div className="column-toggles">
                <span className="column-toggle-label">Stato:</span>
                <button className={`column-toggle-btn ${tuttiAttivi ? "active" : ""}`} onClick={() => setStatiAttivi(tuttiAttivi ? [] : STATI)}>
                    <IconaChip attiva={tuttiAttivi} />
                    {tuttiAttivi ? "Deseleziona tutti" : "Seleziona tutti"}
                </button>
                {STATI.map((stato) => (
                    <button key={stato} className={`column-toggle-btn ${statiAttivi.includes(stato) ? "active" : ""}`} onClick={() => toggleStato(stato)}>
                        <IconaChip attiva={statiAttivi.includes(stato)} />
                        {stato} <span className="mt-chip-conteggio">{conteggi[stato]}</span>
                    </button>
                ))}
            </div>

            {viewMode === "table" ? (
                <div className={`mt-table-container${fullscreen ? " mt-fullscreen" : ""}`}>
                    <RotateDevice active={fullscreen} maxWidth={1024} />
                    <div className="mt-table-topbar">
                        <span className="mt-table-titolo">
                            {nf(filtrate.length)} {filtrate.length === 1 ? "mezzo" : "mezzi"}
                            {filtrate.length !== righe.length ? <span className="mt-muted"> su {nf(righe.length)}</span> : null}
                        </span>
                        <div className="mt-table-azioni">
                            {bottoneExport}
                            <FullscreenBtn attivo={fullscreen} onClick={() => setFullscreen((v) => !v)} />
                        </div>
                    </div>
                    <div className="mt-table-scroll">
                        <table className="mt-table">
                            <thead>
                                <tr>
                                    {COLONNE.map((c) => (
                                        <th key={c.key} onClick={c.notSortable ? undefined : () => ordinaPer(c.key)} className={c.notSortable ? "" : "ordinabile"}>
                                            {c.label}
                                            {!c.notSortable && (
                                                <span className="mt-sort">{ordine.key === c.key ? (ordine.dir === "asc" ? "▲" : "▼") : "⇅"}</span>
                                            )}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {righePagina.length === 0 && (
                                    <tr><td colSpan={COLONNE.length} className="mt-vuoto">Nessun mezzo con questi filtri</td></tr>
                                )}
                                {righePagina.map((r) => (
                                    <tr key={`${r.id_veicolo}-${r.tipo_manutenzione || ""}`}>
                                        <td className="mt-targa">{r.targa}</td>
                                        <td>{r.tipologia}<div className="mt-sub">{r.sottotipologia}</div></td>
                                        <td>
                                            {dataIt(r.data_ultimo)}
                                            {r.km_ultimo !== null && <div className="mt-sub">{nf(r.km_ultimo)} km</div>}
                                        </td>
                                        <td className="mt-num">
                                            {r.km_oggi !== null ? `${nf(r.km_oggi)} km` : "—"}
                                            {r.stato === "Da verificare" && (
                                                <span className="mt-avviso" title={`${r.motivo || ""}${r.ultimo_msg_webfleet ? ` (ultimo segnale ${dataIt(r.ultimo_msg_webfleet)})` : ""}`}>⚠</span>
                                            )}
                                        </td>
                                        <td className="mt-num">
                                            {r.soglia !== null ? `${nf(r.soglia)} km` : "—"}
                                            {r.intervallo ? <div className="mt-sub">ogni {nf(r.intervallo)} km</div> : null}
                                        </td>
                                        <td className={`mt-num${r.residuo !== null && r.residuo <= 0 ? " mt-superato" : ""}`}>{testoKmMancanti(r)}</td>
                                        <td><BarraIntervallo r={r} /></td>
                                        <td>
                                            {r.data_stimata ? dataIt(r.data_stimata) : "—"}
                                            {r.km_giorno ? <div className="mt-sub">{nf(Math.round(r.km_giorno))} km/giorno</div> : null}
                                        </td>
                                        <td><span className={`mt-badge mt-badge-${CLASSE_STATO[r.stato]}`}>{r.stato}</span></td>
                                        <td><ScadenzaInteracta r={r} /></td>
                                        <td className="mt-note">{r.motivo || ""}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {/* Dentro il contenitore della tabella: resta visibile anche a tutto schermo */}
                    {paginazione}
                </div>
            ) : (
                <>
                    {righePagina.length === 0 ? (
                        <div className="mt-vuoto mt-vuoto-card">Nessun mezzo con questi filtri</div>
                    ) : (
                        <div className="cards-grid-compact">
                            {righePagina.map((r) => (
                                <div key={`${r.id_veicolo}-${r.tipo_manutenzione || ""}`} className="data-card-compact">
                                    <div className="card-header-compact">
                                        <span className="vehicle-icon-large">{iconaMezzo(r.tipologia)}</span>
                                        <div className="card-title-info">
                                            <div className="card-title-compact">{r.targa}</div>
                                            <div className="card-subtitle-compact">{r.tipologia}{r.sottotipologia ? ` · ${r.sottotipologia}` : ""}</div>
                                        </div>
                                        <span className={`mt-badge mt-badge-${CLASSE_STATO[r.stato]}`}>{r.stato}</span>
                                    </div>
                                    <div className="card-body-compact">
                                        <div className="card-row-maintenance">
                                            <span className="card-label-bimestre">Ultimo tagliando</span>
                                            <span className="mt-card-valore">{dataIt(r.data_ultimo)}{r.km_ultimo !== null ? ` · ${nf(r.km_ultimo)} km` : ""}</span>
                                        </div>
                                        <div className="card-row-maintenance">
                                            <span className="card-label-bimestre">Km attuali</span>
                                            <span className="mt-card-valore">
                                                {r.km_oggi !== null ? `${nf(r.km_oggi)} km` : "—"}
                                                {r.stato === "Da verificare" && <span className="mt-avviso" title={r.motivo || ""}>⚠</span>}
                                            </span>
                                        </div>
                                        <div className="card-row-maintenance">
                                            <span className="card-label-bimestre">Prossimo a</span>
                                            <span className="mt-card-valore">{r.soglia !== null ? `${nf(r.soglia)} km` : "—"}</span>
                                        </div>
                                        <div className="card-row-maintenance">
                                            <span className="card-label-bimestre">Km mancanti</span>
                                            <span className={`mt-card-valore${r.residuo !== null && r.residuo <= 0 ? " mt-superato" : ""}`}>{testoKmMancanti(r)}</span>
                                        </div>
                                        {r.percentuale !== null && (
                                            <div className="card-row-maintenance">
                                                <span className="card-label-bimestre">Intervallo</span>
                                                <span className="mt-card-barra"><BarraIntervallo r={r} /></span>
                                            </div>
                                        )}
                                        <div className="card-row-maintenance">
                                            <span className="card-label-bimestre">Data stimata</span>
                                            <span className="mt-card-valore">{r.data_stimata ? dataIt(r.data_stimata) : "—"}</span>
                                        </div>
                                        <div className="card-row-maintenance">
                                            <span className="card-label-bimestre">Scadenza Interacta</span>
                                            <ScadenzaInteracta r={r} />
                                        </div>
                                        {r.motivo && <div className="mt-card-nota">{r.motivo}</div>}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    <div className="mt-pagination-card">{paginazione}</div>
                </>
            )}
        </div>
    );
};

/**
 * Contenuto della finestra "i" quando e' aperta la tab Tagliandi (la renderizza la pagina
 * madre dentro il suo modal). Sta qui per restare vicino al codice che descrive.
 */
export const InfoTagliandi = () => (
    <>
        <div className="modal-section">
            <h4>Cosa mostra la tab Tagliandi?</h4>
            <p>
                La situazione dei <strong>tagliandi a chilometri</strong> mezzo per mezzo: quando è stato fatto l'ultimo,
                quanti km ha percorso il mezzo da allora e quanti ne mancano al prossimo. I dati vengono
                <strong> ricalcolati ogni mattina alle 06:30</strong> da un'automazione che legge i km da Webfleet,
                i piani di manutenzione e lo scadenziario automezzi di Interacta. La stessa automazione crea su
                Interacta la scadenza "Tagliando" quando un mezzo si avvicina alla soglia.
            </p>
        </div>

        <div className="modal-section">
            <h4>Come si calcola</h4>
            <ul style={{ marginLeft: "20px", marginTop: "10px", lineHeight: "1.8" }}>
                <li><strong>Ultimo tagliando:</strong> l'ultima scadenza "Tagliando" chiusa su Interacta, con i km scritti dall'officina alla chiusura</li>
                <li><strong>Prossimo a:</strong> km dell'ultimo tagliando + l'intervallo del piano di manutenzione della tipologia (es. Porter ogni 8.000 km)</li>
                <li><strong>Km mancanti:</strong> prossimo tagliando − km attuali letti da Webfleet; se negativo, la soglia è già stata superata</li>
                <li><strong>Intervallo percorso:</strong> quanta parte dell'intervallo è già stata fatta (oltre il 100% = in ritardo)</li>
                <li><strong>Data stimata:</strong> quando il mezzo arriverà alla soglia mantenendo la media di km al giorno degli ultimi 30 giorni. È una stima, non una scadenza</li>
            </ul>
        </div>

        <div className="modal-section">
            <h4>Gli stati</h4>
            <ul style={{ marginLeft: "20px", marginTop: "10px", lineHeight: "1.8" }}>
                <li><strong>Scaduto:</strong> la soglia del tagliando è già stata superata, oppure su Interacta non c'è nessun tagliando registrato (anche se la pratica è già aperta o presa in carico). I mezzi mai tagliandati sono in cima</li>
                <li><strong>In scadenza:</strong> mancano 500 km o meno (è il momento in cui viene creata la scadenza su Interacta)</li>
                <li><strong>Entro 30 giorni:</strong> al ritmo attuale arriverà alla soglia entro un mese</li>
                <li><strong>In regola:</strong> nessuna scadenza vicina</li>
                <li><strong>Da verificare:</strong> la centralina Webfleet non trasmette da oltre 30 giorni, quindi i km potrebbero essere vecchi, oppure i km Webfleet sono inferiori a quelli registrati all'ultimo tagliando</li>
                <li><strong>Non monitorato:</strong> il calcolo non è possibile; il motivo è nella colonna Note (mezzo non collegato a Webfleet, nessun piano per la tipologia, ultimo tagliando chiuso senza km…)</li>
            </ul>
            <p style={{ marginTop: "10px" }}>
                Le <strong>spazzatrici</strong> non compaiono: la loro manutenzione è a ore motore ed è gestita esternamente.
            </p>
        </div>

        <div className="modal-section">
            <h4>Scadenza Interacta</h4>
            <p>
                Se per il mezzo c'è una scadenza "Tagliando" ancora aperta su Interacta, la colonna ne mostra lo stato
                (Inserita, Da Fare, Presa in carico…). <strong>Cliccando sullo stato si apre la scadenza su Interacta</strong> in
                una nuova scheda (serve essere collegati a Interacta), per gestirla direttamente da lì. Quando l'officina
                chiude la scadenza indicando i km, dalla mattina successiva il mezzo riparte da quel tagliando.
            </p>
        </div>

        <div className="modal-section">
            <h4>Filtri, vista ed export</h4>
            <p>
                Le <strong>chip di stato</strong> filtrano subito; i campi di <strong>Mostra Filtri</strong> (targa, tipologia,
                sottotipologia, scadenza Interacta) si applicano con il pulsante <strong>Applica</strong>. Si può scegliere tra
                vista a <strong>tabella</strong> (ordinabile cliccando sulle intestazioni, anche a tutto schermo) e a
                <strong> cards</strong>. <strong>Esporta Excel</strong> scarica tutte le righe filtrate, nello stesso ordine della
                tabella, con i filtri attivi scritti in testa al file.
            </p>
            <p style={{ marginTop: "10px" }}>
                Se in alto compare l'avviso <strong>"Dati non aggiornati"</strong>, il calcolo del mattino non è arrivato e i
                numeri sono quelli dell'ultimo giorno disponibile.
            </p>
        </div>
    </>
);

export default ManutenzioneTagliandi;
